import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import logoSvg from '../../../assets/weaversworld-logo.svg';
import './Header.css';

/**
 * Application header with navigation and authentication controls
 */
const Header = ({ audioPlaying, onAudioToggle }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { isAuthenticated, user, activeChildProfile, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.email) return '?';
    
    const email = user.email;
    // Get first character of email
    return email.charAt(0).toUpperCase();
  };
  
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  
  const closeDropdown = () => {
    setDropdownOpen(false);
  };
  
  // Handle clicks outside of dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if click is outside dropdown and outside the button
      if (
        dropdownOpen && 
        dropdownRef.current && 
        buttonRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    
    // Add event listener when dropdown is open
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);
  
  return (
    <header className="header">
      <div className="header__left-section">
        <Link to="/" className="header__logo-link">
          <img 
            src={logoSvg} 
            alt="Weaver's World"
            className="header__logo-image"
          />
        </Link>
        
        {activeChildProfile && (
          <Link to="/stories" className="header__stories-chip">
            {activeChildProfile.name}
          </Link>
        )}
        
        {onAudioToggle && (
          <button 
            className={`header__audio-toggle ${audioPlaying ? 'playing' : ''}`}
            onClick={onAudioToggle}
            aria-label={audioPlaying ? 'Mute background music' : 'Play background music'}
          >
            {audioPlaying ? '🎵' : '🔇'}
          </button>
        )}
      </div>
      
      <nav className="header__nav">
        {/* Nav links moved to left section */}
      </nav>
      
      <div className="header__auth">
        {isAuthenticated ? (
          <div className="header__user-menu">
            <button 
              ref={buttonRef}
              className="header__user-button"
              onClick={toggleDropdown}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <span className="header__user-avatar">
                {getUserInitials()}
              </span>
            </button>
            
            {dropdownOpen && (
              <div ref={dropdownRef} className="header__dropdown">
                <Link 
                  to="/profile" 
                  className="header__dropdown-item"
                  onClick={closeDropdown}
                >
                  My Profile
                </Link>
                
                <a 
                  href="#" 
                  className="header__dropdown-item header__signout-link"
                  onClick={(e) => {
                    e.preventDefault();
                    closeDropdown();
                    handleLogout();
                  }}
                >
                  Sign Out
                </a>
              </div>
            )}
          </div>
        ) : (
          <>
            <Link to="/accounts/login">
              <Button variant="text">Sign In</Button>
            </Link>
            <Link to="/accounts/signup">
              <Button variant="primary">Sign Up</Button>
            </Link>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;